/**
 * Attention curve (2026-08-05 ROI pack) — where viewers actually spent
 * their time inside a trailer, from GET /api/demos/:id/attention.
 *
 * Complements the EngagementHeatStrip (per-second reach) with density:
 * the heat strip says "these seconds were seen", this says "these
 * ranges were watched HARD" — the re-watched pricing chapter is the
 * thing a rep leads their follow-up with.
 *
 * Chapter labels arrive when compose persists the trailer chapter
 * timeline (plumbing named in the backend endpoint docblock); v1 shows
 * time ranges.
 */

import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

export default function AttentionCurve({ authToken, jobId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/demos/${jobId}/attention`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setData(j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jobId, authToken]);

  // Render nothing until there's enough signal to be meaningful —
  // same honesty rule as the heat strip's empty state.
  if (!data || data.sessions < 2 || !data.curve.length) return null;

  const max = Math.max(...data.curve.map((c) => c.density), 1);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted, #64748b)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          Attention density
        </span>
        <span style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)' }}>{data.sessions} viewing sessions</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 34, marginTop: 6 }}>
        {data.curve.map((c) => (
          <div
            key={c.atSeconds}
            title={`${fmt(c.atSeconds)} · watched in ${c.density} session${c.density === 1 ? '' : 's'}`}
            style={{
              flex: 1,
              height: `${Math.max(6, (c.density / max) * 100)}%`,
              borderRadius: 2,
              background: '#6366f1',
              opacity: c.density === 0 ? 0.12 : 0.25 + (c.density / max) * 0.75,
            }}
          />
        ))}
      </div>
      {data.topRanges.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--room-text-muted, #64748b)', marginTop: 6 }}>
          Most re-watched:{' '}
          {data.topRanges.slice(0, 3).map((r, i) => (
            <span key={r.fromSeconds} style={{ fontWeight: 600, color: '#4f46e5' }}>
              {i > 0 && ' · '}
              {r.chapter
                ? `${r.chapter} (${fmt(r.fromSeconds)}–${fmt(r.toSeconds)})`
                : `${fmt(r.fromSeconds)}–${fmt(r.toSeconds)}`}
            </span>
          ))}
          <span style={{ color: 'var(--room-text-subtle, #94a3b8)' }}> — lead your follow-up with what they replayed.</span>
        </div>
      )}
    </div>
  );
}
